import { useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Callout,
  Dialog,
  Flex,
  Heading,
  Text,
} from "@radix-ui/themes";
import {
  AlertTriangle,
  Car,
  CheckCircle2,
  Copy,
  MailPlus,
  Pencil,
  Trash,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";
import { badgeColors } from "@/shared/constants/constants";

// ── Types ─────────────────────────────────────────────────────────────────────

interface RosterMember {
  user_id: number;
  email: string;
  is_owner: boolean;
  driver_car: { id: number; display_name: string } | null;
}

interface PendingInvite {
  id: number;
  email: string;
  invited_at: string;
}

interface EventHistoryItem {
  id: number;
  status: string;
  event_name: string;
  event_date: string | null;
  attended: boolean;
}

interface Props {
  participant: {
    id: number;
    slug: string;
    name: string;
    location: string | null;
    instagram: string | null;
  };
  team: {
    id: number;
    description: string;
    logo_url: string | null;
  };
  my_car_id: number | null;
  my_cars: { id: number; display_name: string }[];
  roster: RosterMember[];
  pending_invites: PendingInvite[];
  event_history: EventHistoryItem[];
  stats: {
    member_count: number;
    pending_count: number;
    events_entered: number;
    events_attended: number;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: "10.5px",
      fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--gray-9)",
      marginBottom: "0.75rem",
    }}>
      {children}
    </p>
  );
}

function daysAgo(isoStr: string) {
  const diff = Math.floor((Date.now() - new Date(isoStr).getTime()) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return `${diff} days ago`;
}

const STATUS_LABELS: Record<string, string> = {
  pending:   "Under review",
  approved:  "Accepted",
  rejected:  "Not accepted",
  withdrawn: "Withdrawn",
  draft:     "Draft",
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function MyTeam({
  participant,
  team,
  my_car_id,
  my_cars,
  roster,
  pending_invites,
  event_history,
  stats,
}: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCarId, setSelectedCarId] = useState<number | null>(my_car_id);
  const [savingCar, setSavingCar] = useState(false);

  function copyTeamLink() {
    navigator.clipboard.writeText(`${window.location.origin}/teams/${participant.slug}`);
  }

  function handleRemoveMember(userId: number, email: string) {
    if (confirm(`Remove ${email} from the team?`)) {
      router.delete(`/teams/${participant.slug}/members/${userId}`);
    }
  }

  function saveCarSelection() {
    setSavingCar(true);
    router.patch(
      `/teams/${participant.slug}/car`,
      { driver_car_id: selectedCarId },
      { onFinish: () => setSavingCar(false) }
    );
  }

  function handleResendInvite(inviteId: number) {
    router.post(`/teams/${participant.slug}/invites/${inviteId}/resend`);
  }

  function handleCancelInvite(inviteId: number) {
    if (confirm("Cancel this invitation?")) {
      router.delete(`/teams/${participant.slug}/invites/${inviteId}`);
    }
  }

  function handleDeleteTeam() {
    router.delete(`/teams/${participant.slug}`);
    setDeleteDialogOpen(false);
  }

  return (
    <div className="flex">
      <Head title={`${participant.name} — My Team`} />
      <div className="grow">
        <main style={{ maxWidth: 900, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>

          {/* ── Hero ── */}
          <Card style={{ marginBottom: "1.5rem" }}>
            <Flex gap="4" align="start">
              <Avatar
                size="6"
                src={team.logo_url ?? undefined}
                fallback={participant.name[0].toUpperCase()}
                radius="full"
              />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Flex justify="between" align="start" gap="3" wrap="wrap">
                  <Box>
                    <Flex align="center" gap="2" mb="1">
                      <Heading size="6">{participant.name}</Heading>
                      <Badge size="1" variant="soft" radius="full">Owner</Badge>
                    </Flex>
                    {participant.location && (
                      <Text size="2" color="gray">{participant.location}</Text>
                    )}
                  </Box>
                  <Flex gap="2" wrap="wrap">
                    <Button
                      variant="soft"
                      color="gray"
                      onClick={() => router.visit("/team_participant/edit")}
                    >
                      <Pencil size={14} /> Edit Details
                    </Button>
                    <Button variant="soft" color="gray" onClick={copyTeamLink}>
                      <Copy size={14} /> Copy team link
                    </Button>
                  </Flex>
                </Flex>
                {team.description && (
                  <Text as="p" size="2" color="gray" mt="3">{team.description}</Text>
                )}
              </Box>
            </Flex>
          </Card>

          {/* ── Stats bar ── */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 10,
            marginBottom: "2rem",
          }}>
            {([
              { num: stats.member_count,    label: "Members" },
              { num: stats.pending_count,   label: "Pending invites" },
              { num: stats.events_entered,  label: "Events entered" },
              { num: stats.events_attended, label: "Events attended" },
            ] as const).map(({ num, label }) => (
              <div key={label} style={{
                background: "var(--color-panel-solid)",
                border: "0.5px solid var(--gray-5)",
                borderRadius: 12,
                padding: "0.875rem 1rem",
              }}>
                <p style={{ fontSize: 22, fontWeight: 500, fontFamily: "monospace", color: "var(--gray-12)" }}>
                  {num}
                </p>
                <p style={{ fontSize: 11, color: "var(--gray-10)", marginTop: 2 }}>{label}</p>
              </div>
            ))}
          </div>

          {/* ── My Participation ── */}
          <section style={{ marginBottom: "2rem" }}>
            <SectionLabel>My Participation</SectionLabel>
            <Card>
              <Text as="p" size="2" weight="medium" mb="3">Car for this team</Text>
              <Text as="p" size="2" color="gray" mb="4">
                Select which car you're running under this team. This appears on the team roster.
              </Text>

              {my_cars.length === 0 ? (
                <Callout.Root color="gray" variant="surface" mb="4">
                  <Callout.Icon><Car size={16} /></Callout.Icon>
                  <Callout.Text>
                    You don't have any cars in your garage yet.{" "}
                    <Link href="/driver_cars/new" style={{ color: "var(--accent-11)" }}>
                      Add a car →
                    </Link>
                  </Callout.Text>
                </Callout.Root>
              ) : (
                <Flex direction="column" gap="2" mb="4">
                  {my_cars.map((car) => (
                    <label
                      key={car.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "0.75rem 1rem",
                        borderRadius: "var(--radius-3)",
                        border: selectedCarId === car.id
                          ? "1.5px solid var(--accent-8)"
                          : "1px solid var(--gray-5)",
                        background: selectedCarId === car.id ? "var(--accent-2)" : "var(--color-panel-solid)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="my-car"
                        value={car.id}
                        checked={selectedCarId === car.id}
                        onChange={() => setSelectedCarId(car.id)}
                        style={{ accentColor: "var(--accent-9)" }}
                      />
                      <Flex align="center" gap="2">
                        <Car size={14} color="var(--gray-9)" />
                        <Text size="2">{car.display_name}</Text>
                      </Flex>
                    </label>
                  ))}
                  {selectedCarId !== null && (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "0.75rem 1rem",
                        borderRadius: "var(--radius-3)",
                        border: "1px solid var(--gray-5)",
                        background: "var(--color-panel-solid)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="my-car"
                        value=""
                        checked={selectedCarId === null}
                        onChange={() => setSelectedCarId(null)}
                        style={{ accentColor: "var(--accent-9)" }}
                      />
                      <Text size="2" color="gray">No car (unset)</Text>
                    </label>
                  )}
                </Flex>
              )}

              {my_cars.length > 0 && (
                <Flex justify="end">
                  <Button onClick={saveCarSelection} disabled={savingCar}>
                    <CheckCircle2 size={14} /> Save
                  </Button>
                </Flex>
              )}
            </Card>
          </section>

          {/* ── Pending Invitations ── */}
          <section style={{ marginBottom: "2rem" }}>
            <Flex justify="between" align="center" mb="3">
              <Flex align="center" gap="2">
                <SectionLabel>Pending Invitations</SectionLabel>
                {pending_invites.length > 0 && (
                  <Badge size="1" variant="soft" color="amber" radius="full" style={{ marginBottom: "0.75rem" }}>
                    {pending_invites.length}
                  </Badge>
                )}
              </Flex>
              <Button
                size="1"
                variant="soft"
                color="gray"
                style={{ marginBottom: "0.75rem" }}
                onClick={() => router.visit("/team_participant/edit")}
              >
                <UserPlus size={13} /> Invite someone
              </Button>
            </Flex>
            <Card>
              {pending_invites.length === 0 ? (
                <Callout.Root color="gray" variant="surface">
                  <Callout.Icon><MailPlus size={16} /></Callout.Icon>
                  <Callout.Text>No pending invitations. Use the invite button to add people to your team.</Callout.Text>
                </Callout.Root>
              ) : (
                pending_invites.map((invite, i) => (
                  <Flex
                    key={invite.id}
                    align="center"
                    justify="between"
                    gap="3"
                    py="3"
                    style={{
                      borderBottom: i < pending_invites.length - 1 ? "1px solid var(--gray-4)" : "none",
                    }}
                  >
                    <Flex align="center" gap="2">
                      <Avatar
                        size="2"
                        fallback={invite.email[0].toUpperCase()}
                        color="amber"
                        radius="full"
                      />
                      <Box>
                        <Text size="2" weight="medium">{invite.email}</Text>
                        <Text as="p" size="1" color="gray">Invited {daysAgo(invite.invited_at)}</Text>
                      </Box>
                    </Flex>
                    <Flex align="center" gap="2">
                      <Badge size="1" variant="soft" color="amber" radius="full">Pending accept</Badge>
                      <Button
                        size="1"
                        variant="ghost"
                        color="gray"
                        title="Resend invitation"
                        onClick={() => handleResendInvite(invite.id)}
                      >
                        <MailPlus size={13} />
                      </Button>
                      <Button
                        size="1"
                        variant="ghost"
                        color="red"
                        title="Cancel invitation"
                        onClick={() => handleCancelInvite(invite.id)}
                      >
                        <Trash size={13} />
                      </Button>
                    </Flex>
                  </Flex>
                ))
              )}
            </Card>
          </section>

          {/* ── Roster ── */}
          <section style={{ marginBottom: "2rem" }}>
            <SectionLabel>Roster</SectionLabel>
            <Card>
              {roster.length === 0 ? (
                <Callout.Root color="gray" variant="surface">
                  <Callout.Icon><Users size={16} /></Callout.Icon>
                  <Callout.Text>No members yet. Invite people from the team edit form.</Callout.Text>
                </Callout.Root>
              ) : (
                roster.map((member, i) => (
                  <Flex
                    key={member.user_id}
                    align="center"
                    justify="between"
                    gap="3"
                    py="3"
                    style={{
                      borderBottom: i < roster.length - 1 ? "1px solid var(--gray-4)" : "none",
                    }}
                  >
                    <Flex align="center" gap="2" style={{ minWidth: 0 }}>
                      <Avatar
                        size="2"
                        fallback={member.email[0].toUpperCase()}
                        color={member.is_owner ? undefined : "gray"}
                        radius="full"
                      />
                      <Box style={{ minWidth: 0 }}>
                        <Flex align="center" gap="2">
                          <Text size="2" weight="medium">{member.email}</Text>
                          {member.is_owner && (
                            <Badge size="1" variant="soft" radius="full">Owner</Badge>
                          )}
                        </Flex>
                        {member.driver_car && (
                          <Flex align="center" gap="1" mt="1">
                            <Car size={11} color="var(--gray-9)" />
                            <Text size="1" color="gray">{member.driver_car.display_name}</Text>
                          </Flex>
                        )}
                      </Box>
                    </Flex>
                    {!member.is_owner && (
                      <Button
                        size="1"
                        variant="ghost"
                        color="gray"
                        title="Remove member"
                        onClick={() => handleRemoveMember(member.user_id, member.email)}
                      >
                        <UserMinus size={14} />
                      </Button>
                    )}
                  </Flex>
                ))
              )}
            </Card>
          </section>

          {/* ── Event History ── */}
          {event_history.length > 0 && (
            <section style={{ marginBottom: "2rem" }}>
              <SectionLabel>Event History</SectionLabel>
              <Card>
                {event_history.map((item, i) => (
                  <Flex
                    key={item.id}
                    align="center"
                    justify="between"
                    gap="3"
                    py="3"
                    style={{
                      borderBottom: i < event_history.length - 1 ? "1px solid var(--gray-4)" : "none",
                    }}
                  >
                    <Box>
                      <Text size="2" weight="medium">{item.event_name}</Text>
                      {item.event_date && (
                        <Text as="p" size="1" color="gray">{item.event_date}</Text>
                      )}
                    </Box>
                    <Badge
                      size="1"
                      variant="soft"
                      color={item.attended ? "green" : (badgeColors[item.status] as any) || "gray"}
                      radius="full"
                    >
                      {item.attended ? "Attended" : (STATUS_LABELS[item.status] ?? item.status)}
                    </Badge>
                  </Flex>
                ))}
              </Card>
            </section>
          )}

          {/* ── Danger Zone ── */}
          <section>
            <SectionLabel>Danger Zone</SectionLabel>
            <Card style={{ border: "1px solid var(--red-6)" }}>
              <Flex justify="between" align="center" gap="4">
                <Box>
                  <Text size="2" weight="medium" color="red">Delete team</Text>
                  <Text as="p" size="1" color="gray" mt="1">
                    Permanently deletes the team, roster, and all associated data. This cannot be undone.
                  </Text>
                </Box>
                <Dialog.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                  <Dialog.Trigger>
                    <Button variant="soft" color="red">
                      <Trash size={14} /> Delete team
                    </Button>
                  </Dialog.Trigger>
                  <Dialog.Content maxWidth="420px">
                    <Flex align="center" gap="3" mb="4">
                      <Flex
                        align="center"
                        justify="center"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: "var(--red-3)",
                          color: "var(--red-11)",
                          flexShrink: 0,
                        }}
                      >
                        <AlertTriangle size={18} />
                      </Flex>
                      <Dialog.Title>Delete {participant.name}?</Dialog.Title>
                    </Flex>
                    <Dialog.Description size="2" color="gray" mb="5">
                      This will permanently delete the team and remove all members from the roster.
                      Applications submitted under this team will also be removed.
                      This cannot be undone.
                    </Dialog.Description>
                    <Flex gap="2" justify="end">
                      <Dialog.Close>
                        <Button variant="soft" color="gray">Cancel</Button>
                      </Dialog.Close>
                      <Button color="red" onClick={handleDeleteTeam}>
                        Yes, delete team
                      </Button>
                    </Flex>
                  </Dialog.Content>
                </Dialog.Root>
              </Flex>
            </Card>
          </section>

        </main>
      </div>
    </div>
  );
}
